import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './DocumentSearch.css';

const API_BASE = process.env.REACT_APP_API_BASE || 'http://localhost:8000/api';

function DocumentSearch({ onDocumentSelect, showToast, selectedDocType, selectedDocVersion }) {
  const [documentName, setDocumentName] = useState('');
  const [documentVersion, setDocumentVersion] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(true);
  const [pendingDownload, setPendingDownload] = useState(null);

  const handleSearch = async () => {
    if (!documentName && !documentVersion) {
      showToast('Please enter at least a document name or version', 'error');
      return;
    }

    setIsSearching(true);
    try {
      const params = new URLSearchParams();
      if (documentName) params.append('document_name', documentName);
      if (documentVersion) params.append('document_version', documentVersion);
      params.append('limit', '20');

      const response = await axios.get(`${API_BASE}/search/search?${params.toString()}`);
      setSearchResults(response.data);
      
      if (response.data.length === 0) {
        showToast('No documents found matching your search criteria', 'info');
      }
    } catch (error) {
      console.error('Error searching documents:', error);
      showToast('Error searching documents: ' + (error.response?.data?.detail || error.message), 'error');
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  };

  const handleResultClick = async (result) => {
    if (!result.labelboxDataRowId || !result.labelboxDataRowId.trim()) {
      showToast('No Labelbox data row ID found for this document', 'error');
      return;
    }

    if (!result.documentType || !result.documentType.trim()) {
      showToast('No document type found for this document', 'error');
      return;
    }

    // Version is optional - use empty string if not present
    const documentVersion = result.documentTemplateVersion && result.documentTemplateVersion.trim() 
      ? result.documentTemplateVersion 
      : null;

    // Check if data already exists (only if version exists)
    try {
      const checkUrl = documentVersion 
        ? `${API_BASE}/search/check-exists/${result.documentType}?version=${encodeURIComponent(documentVersion)}`
        : `${API_BASE}/search/check-exists/${result.documentType}`;
      
      const checkResponse = await axios.get(checkUrl);
      
      // Only show popup if both PDF and data exist
      if (checkResponse.data.pdf_exists && checkResponse.data.data_exists) {
        // Ask user if they want to re-download
        const versionText = documentVersion ? ` (${documentVersion})` : '';
        const shouldDownload = window.confirm(
          `Document "${result.documentFullName || result.name}"${versionText} already exists locally.\n\n` +
          `PDF: Exists\n` +
          `Data: Exists\n\n` +
          `Do you want to download it again?`
        );
        
        if (!shouldDownload) {
          // Just select the existing document
          onDocumentSelect(result.documentType, documentVersion);
          setIsCollapsed(true);
          return;
        }
      } else if (checkResponse.data.pdf_exists && !checkResponse.data.data_exists) {
        // PDF exists but data is missing - automatically download to get the data
        showToast('PDF exists but data is missing. Downloading data...', 'info');
        // Continue with download below
      } else if (!checkResponse.data.pdf_exists && checkResponse.data.data_exists) {
        // Data exists but PDF is missing - automatically download to get the PDF
        showToast('Data exists but PDF is missing. Downloading PDF...', 'info');
        // Continue with download below
      }
      // If neither exists, just continue with download
    } catch (error) {
      console.error('Error checking existing data:', error);
      // Continue with download if check fails
    }

    // Proceed with download
    setIsLoading(true);
    try {
      console.log('Downloading PDF with:', {
        labelbox_data_row_id: result.labelboxDataRowId,
        document_type: result.documentType,
        document_version: documentVersion
      });
      
      // Download PDF from Labelbox
      const downloadPayload = {
        labelbox_data_row_id: result.labelboxDataRowId,
        document_type: result.documentType,
        force_download: true
      };
      
      // Only include version if it exists
      if (documentVersion) {
        downloadPayload.document_version = documentVersion;
      }
      
      const downloadResponse = await axios.post(`${API_BASE}/search/download`, downloadPayload);

      if (downloadResponse.data.success) {
        showToast(`PDF and data downloaded: ${downloadResponse.data.filename}`, 'success');
        // Notify parent component about the selected document
        // Small delay to ensure PDF is saved before loading
        setTimeout(() => {
          onDocumentSelect(result.documentType, documentVersion);
          // Collapse the search panel after selection
          setIsCollapsed(true);
        }, 500);
      } else if (downloadResponse.data.already_exists) {
        // Data exists but we tried to download anyway
        showToast('Using existing document data', 'info');
        onDocumentSelect(result.documentType, documentVersion);
        setIsCollapsed(true);
      }
    } catch (error) {
      console.error('Error downloading document:', error);
      const errorMessage = error.response?.data?.detail || error.message || 'Unknown error occurred';
      showToast('Error downloading PDF: ' + errorMessage, 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  return (
    <div className="document-search">
      <div 
        className="search-header"
        onClick={() => setIsCollapsed(!isCollapsed)}
        role="button"
        tabIndex={0}
        onKeyPress={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            setIsCollapsed(!isCollapsed);
          }
        }}
      >
        <div>
          <h3>Search Documents</h3>
          {selectedDocType && (
            <p className="selected-doc-indicator">
              Selected: <strong>{selectedDocType}</strong>
              {selectedDocVersion && ` (${selectedDocVersion})`}
            </p>
          )}
        </div>
        <span className="collapse-toggle">
          {isCollapsed ? '▼' : '▲'}
        </span>
      </div>
      
      {!isCollapsed && (
        <>
          <div className="search-form">
        <div className="search-field">
          <label htmlFor="document-name">Document Name</label>
          <input
            id="document-name"
            type="text"
            placeholder="e.g., Residential Purchase And Sale Agreement"
            value={documentName}
            onChange={(e) => setDocumentName(e.target.value)}
            onKeyPress={handleKeyPress}
          />
        </div>
        
        <div className="search-field">
          <label htmlFor="document-version">Version</label>
          <input
            id="document-version"
            type="text"
            placeholder="e.g., v24_08"
            value={documentVersion}
            onChange={(e) => setDocumentVersion(e.target.value)}
            onKeyPress={handleKeyPress}
          />
        </div>
        
        <button 
          className="btn-search" 
          onClick={handleSearch}
          disabled={isSearching || isLoading}
        >
          {isSearching ? 'Searching...' : 'Search'}
        </button>
      </div>

      {isLoading && (
        <div className="loading-indicator">
          Downloading PDF...
        </div>
      )}

      {searchResults.length > 0 && (
        <div className="search-results">
          <h4>Search Results ({searchResults.length})</h4>
          <div className="results-list">
            {searchResults.map((result) => (
              <div
                key={result._id}
                className="result-item"
                onClick={() => handleResultClick(result)}
              >
                <div className="result-header">
                  <span className="result-name">{result.documentFullName || result.name}</span>
                  {result.documentTemplateVersion && (
                    <span className="result-version">v{result.documentTemplateVersion}</span>
                  )}
                </div>
                <div className="result-details">
                  <span className="result-type">{result.documentType}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
        </>
      )}
    </div>
  );
}

export default DocumentSearch;

